/**
 * Whether the player has asked for less motion — the operating system's
 * reduce-motion setting, as `prefers-reduced-motion` reports it.
 *
 * What moves by CSS needs no reader: every --duration-* token collapses to
 * zero under that setting (tokens.css), and a script that times its motion by
 * a token (motionTiming) takes no time either. This is for motion a script
 * drives that no token times — a typewriter revealing a line letter by
 * letter, a count held back so its tile can turn over, a reaction's Lottie, a
 * glide of the tree or of the globe's camera — so it can arrive at its end at
 * once instead. `motion-ignores-reduced` reads the scripts for the ways one
 * could run past it.
 */

import { useSyncExternalStore } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

function subscribe(listener: () => void): () => void {
  const media = window.matchMedia(QUERY);
  media.addEventListener('change', listener);
  return () => media.removeEventListener('change', listener);
}

function snapshot(): boolean {
  return window.matchMedia(QUERY).matches;
}

/** True while the player asks for reduced motion; the caller re-renders when that changes. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, snapshot);
}
